import React from "react";
import { Button, Callout, Checkbox, Dialog, Field, Input, Textarea } from "../components/DesignSystem";
import { errorMessage } from "../services/errors";
import { usePlannerData } from "../state/PlannerDataContext";
import { useSubmitGuard } from "./common";

const ITEM_CATALOG = ["Invitations", "Save-the-dates", "Menus", "Place cards", "Table numbers", "Thank-you cards", "Favours & gifts"];

/**
 * Contextual "Request a Pockets of Beige quotation" (Wave 4 §13) -- surfaced from Stationery and
 * Gifts/Favours, the two screens the spec calls out. Quantities (event count, invitation count,
 * household count) are derived server-side from the wedding's own data, never asked for here --
 * this form only captures what the couple actually wants (items/style/dates/location).
 */
export function RequestQuoteAction({ defaultItems = [], buttonLabel = "Request a Pockets of Beige quotation" }: { defaultItems?: string[]; buttonLabel?: string }) {
  const { service } = usePlannerData();
  const [open, setOpen] = React.useState(false);
  const [items, setItems] = React.useState<string[]>(defaultItems);
  const [styleNotes, setStyleNotes] = React.useState("");
  const [preferredDates, setPreferredDates] = React.useState("");
  const [location, setLocation] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [sent, setSent] = React.useState(false);
  const { submitting, guardedSubmit } = useSubmitGuard();

  function toggle(item: string) {
    setItems((prev) => (prev.includes(item) ? prev.filter((i) => i !== item) : [...prev, item]));
  }

  async function submit() {
    if (items.length === 0) {
      setError("Choose at least one item to request a quotation for.");
      return;
    }
    await guardedSubmit(async () => {
      setError(null);
      try {
        await service.commerce.requestQuote({ requestedItems: items, styleNotes: styleNotes || undefined, preferredDates: preferredDates || undefined, location: location || undefined });
        setSent(true);
      } catch (err) {
        setError(errorMessage(err));
      }
    });
  }

  return (
    <>
      <Button icon="sparkle" variant="secondary" size="sm" onClick={() => { setOpen(true); setSent(false); }}>{buttonLabel}</Button>
      {open && (
        <Dialog
          title="Request a Pockets of Beige quotation"
          description="Tell us what you're looking for -- we'll follow up with pricing based on your wedding's own details."
          width={480}
          onClose={() => setOpen(false)}
          footer={sent ? <Button onClick={() => setOpen(false)}>Done</Button> : (
            <>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} disabled={submitting}>{submitting ? "Sending..." : "Send request"}</Button>
            </>
          )}
        >
          {sent ? (
            <Callout tone="success" title="Request sent">We've received your quotation request and will be in touch.</Callout>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
              {error && <Callout tone="error" title="Couldn't send that">{error}</Callout>}
              <Field label="What are you interested in?">
                <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
                  {ITEM_CATALOG.map((item) => (
                    <Checkbox key={item} label={item} checked={items.includes(item)} onChange={() => toggle(item)} />
                  ))}
                </div>
              </Field>
              <Field label="Style notes" hint="Optional">
                <Textarea value={styleNotes} onChange={(e) => setStyleNotes(e.target.value)} placeholder="e.g. Minimal, gold foil, floral motifs" />
              </Field>
              <Field label="Preferred dates" hint="Optional">
                <Input value={preferredDates} onChange={(e) => setPreferredDates(e.target.value)} placeholder="e.g. March 2027" />
              </Field>
              <Field label="Location" hint="Optional">
                <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Lahore" />
              </Field>
            </div>
          )}
        </Dialog>
      )}
    </>
  );
}
